import React, { useState, useEffect, useCallback } from 'react';
import * as LucideIcons from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ rating, onRatingChange, interactive = false, size = 'w-6 h-6' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  
  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          onClick={() => interactive && onRatingChange && onRatingChange(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          disabled={!interactive}
        >
          <LucideIcons.Star
            className={`${size} ${
              (interactive ? (hoverRating || rating) : rating) >= star
                ? 'fill-orange-500 text-orange-500'
                : 'fill-gray-200 text-gray-200'
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewForm = ({ onSubmitSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    content: '',
    rating: 5
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submittedReview, setSubmittedReview] = useState(null);
  const [copied, setCopied] = useState(false);

  // Google Business Profile review URL - replace PLACE_ID with actual Place ID
  const googleReviewUrl = "https://search.google.com/local/writereview?placeid=ChIJMbDhM3NHVYYRHtcG_BMyRps";
  const facebookUrl = "https://www.facebook.com/Tandmbuilders/reviews";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/testimonials`, {
        ...formData,
        role: formData.role || 'Customer'
      });
      setSubmitStatus('success');
      setSubmittedReview({ ...formData });
      setFormData({ name: '', role: '', content: '', rating: 5 });
      if (onSubmitSuccess) onSubmitSuccess();
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyReviewToClipboard = () => {
    if (submittedReview) {
      navigator.clipboard.writeText(submittedReview.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const resetForm = () => {
    setSubmitStatus(null);
    setSubmittedReview(null);
  };

  // Show success screen with share options after submission
  if (submitStatus === 'success' && submittedReview) {
    return (
      <div className="space-y-6">
        {/* Success Message */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <LucideIcons.CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-green-800 mb-2">Thank You for Your Review!</h3>
          <p className="text-green-700">Your review has been submitted and will appear on our website shortly.</p>
        </div>

        {/* Share on Other Platforms */}
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
          <div className="text-center mb-6">
            <h4 className="text-lg font-bold text-gray-900 mb-2">Help Us Reach More Customers!</h4>
            <p className="text-gray-600 text-sm">
              Would you mind sharing your review on Google or Facebook? It takes just a minute and helps us grow!
            </p>
          </div>

          {/* Review Preview */}
          <div className="bg-white rounded-lg p-4 mb-6 border border-gray-200">
            <div className="flex items-center space-x-1 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <LucideIcons.Star
                  key={star}
                  className={`w-4 h-4 ${submittedReview.rating >= star ? 'fill-orange-500 text-orange-500' : 'fill-gray-200 text-gray-200'}`}
                />
              ))}
            </div>
            <p className="text-gray-700 text-sm italic">"{submittedReview.content}"</p>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-gray-500">- {submittedReview.name}</span>
              <button
                onClick={copyReviewToClipboard}
                className="flex items-center space-x-1 text-xs text-orange-600 hover:text-orange-700 font-medium"
              >
                {copied ? (
                  <>
                    <LucideIcons.Check className="w-3 h-3" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <LucideIcons.Copy className="w-3 h-3" />
                    <span>Copy Review</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href={googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={copyReviewToClipboard}
              className="flex items-center justify-center space-x-3 px-6 py-4 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <div className="text-left">
                <span className="font-semibold text-gray-900 block">Share on Google</span>
                <span className="text-xs text-gray-500">Review auto-copied to paste</span>
              </div>
              <LucideIcons.ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
            </a>

            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={copyReviewToClipboard}
              className="flex items-center justify-center space-x-3 px-6 py-4 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-600 hover:bg-blue-50 transition-all group"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <div className="text-left">
                <span className="font-semibold text-gray-900 block">Share on Facebook</span>
                <span className="text-xs text-gray-500">Review auto-copied to paste</span>
              </div>
              <LucideIcons.ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
            </a>
          </div>

          <p className="text-center text-xs text-gray-500 mt-4">
            Click a button above to open the review page. Your review has been copied - just paste it!
          </p>
        </div>

        {/* Write Another Review Button */}
        <div className="text-center">
          <button
            onClick={resetForm}
            className="text-orange-600 hover:text-orange-700 font-medium flex items-center space-x-2 mx-auto"
          >
            <LucideIcons.PenLine className="w-4 h-4" />
            <span>Write Another Review</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Name *
          </label>
          <Input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="John Smith"
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location (Optional)
          </label>
          <Input
            type="text"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            placeholder="e.g., Ipswich, Suffolk"
            className="w-full"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Your Rating *
        </label>
        <StarRating
          rating={formData.rating}
          onRatingChange={(rating) => setFormData({ ...formData, rating })}
          interactive={true}
          size="w-8 h-8"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Your Review *
        </label>
        <Textarea
          required
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          placeholder="Tell us about your experience working with T&M Construction..."
          className="w-full min-h-[120px]"
        />
      </div>

      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <LucideIcons.AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-700">Something went wrong. Please try again.</p>
        </div>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3"
      >
        {isSubmitting ? (
          <>
            <LucideIcons.Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <LucideIcons.Send className="w-5 h-5 mr-2" />
            Submit Review
          </>
        )}
      </Button>
    </form>
  );
};

const getSourceIcon = (source) => {
  if (source === 'Google') {
    return (
      <div className="flex items-center space-x-1 text-xs text-gray-500">
        <svg className="w-4 h-4" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        <span>Google Review</span>
      </div>
    );
  } else if (source === 'MyBuilder') {
    return (
      <div className="flex items-center space-x-1 text-xs text-gray-500">
        <div className="w-4 h-4 bg-blue-600 rounded flex items-center justify-center">
          <span className="text-white text-[8px] font-bold">MB</span>
        </div>
        <span>MyBuilder</span>
      </div>
    );
  }
  return null;
};

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const handleSubmitSuccess = () => {
    // Refresh testimonials after submission
    setTimeout(() => {
      fetchTestimonials();
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Reviews & Testimonials - 5 Star Builders Ipswich"
        description="Read 5-star reviews from satisfied T&M Construction customers. Trusted builders in Ipswich, Suffolk with excellent Google & MyBuilder reviews. See why clients recommend us."
        keywords="builders reviews Ipswich, construction testimonials Suffolk, 5 star builders, customer reviews construction, MyBuilder reviews, Google reviews builders, trusted builders testimonials"
        canonical="/testimonials"
      />
      <section className="pt-56 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Client Testimonials</h1>
            <p className="text-xl text-gray-600">
              Read what our satisfied clients have to say about working with T&M Construction
            </p>
            <div className="flex items-center justify-center space-x-4 mt-6">
              <div className="flex items-center space-x-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <LucideIcons.Star key={star} className="w-5 h-5 fill-orange-500 text-orange-500" />
                  ))}
                </div>
                <span className="text-gray-600 font-medium">5.0 Average Rating</span>
              </div>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">{testimonials.length} Reviews</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-20">
              <LucideIcons.Loader2 className="w-10 h-10 animate-spin text-orange-600 mx-auto mb-4" />
              <p className="text-gray-600 text-xl">Loading testimonials...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="bg-white border-2 hover:border-orange-600 transition-all duration-300 shadow-lg">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      <StarRating rating={testimonial.rating} size="w-5 h-5" />
                      {getSourceIcon(testimonial.source)}
                    </div>
                    <CardDescription className="text-gray-700 text-lg leading-relaxed italic">
                      "{testimonial.content}"
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <LucideIcons.User className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-lg">{testimonial.name}</div>
                        <div className="text-sm text-gray-500">{testimonial.role}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Review Submission Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <LucideIcons.MessageSquarePlus className="w-10 h-10 text-orange-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Share Your Experience</h2>
                <p className="text-gray-600">
                  Have you worked with T&M Construction? We'd love to hear about your experience.
                </p>
              </div>

              {!showForm ? (
                <div className="text-center">
                  <Button
                    onClick={() => setShowForm(true)}
                    size="lg"
                    className="bg-orange-600 hover:bg-orange-700 text-white px-8"
                  >
                    <LucideIcons.PenLine className="w-5 h-5 mr-2" />
                    Write a Review
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={() => setShowForm(false)}
                      className="text-gray-500 hover:text-gray-700 flex items-center space-x-1"
                    >
                      <LucideIcons.X className="w-4 h-4" />
                      <span>Cancel</span>
                    </button>
                  </div>
                  <ReviewForm onSubmitSuccess={handleSubmitSuccess} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* External Review Links */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Find Us On</h3>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://www.google.com/search?q=T%26M+Construction+Ipswich"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-orange-600 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="font-medium text-gray-700">Google Reviews</span>
              </a>
              <a
                href="https://www.mybuilder.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-orange-600 transition-colors"
              >
                <div className="w-5 h-5 bg-blue-600 rounded flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold">MB</span>
                </div>
                <span className="font-medium text-gray-700">MyBuilder</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Join Our Happy Clients?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let us transform your property with the same quality and care our clients rave about
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 px-8">
              Get Your Free Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
